import { useMemo, useState } from "react"
import { ImagePlus, LayoutTemplate } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  LOGIN_PAGE_THEMES,
  fileToCompressedDataUrl,
  resolveLoginThemeImage,
  type LoginPageDesign,
} from "@/lib/loginPageDesign"

type LoginPageDesignFieldsProps = {
  value: LoginPageDesign
  onChange: (next: LoginPageDesign) => void
  error?: string
}

function ThemePreview({
  themeId,
  imageUrl,
  accentColor,
}: {
  themeId: number
  imageUrl: string | null
  accentColor: string
}) {
  const photo = imageUrl
  const accent = accentColor || "#0284c7"

  if (themeId === 0) {
    return (
      <div className="relative h-full min-h-[140px] overflow-hidden rounded-lg bg-slate-800">
        {photo ? <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ filter: "saturate(1.3) contrast(1.15)" }} /> : null}
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-slate-950/25 to-transparent" />
        <div className="absolute right-3 top-1/2 h-16 w-12 -translate-y-1/2 rounded-md bg-white shadow" />
      </div>
    )
  }

  if (themeId === 6) {
    return (
      <div className="flex h-full min-h-[140px] overflow-hidden rounded-lg border bg-white">
        <div className="relative w-1/2 bg-slate-200">
          {photo ? <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
        </div>
        <div className="flex w-1/2 items-center justify-center bg-slate-50">
          <div className="h-16 w-12 rounded-md bg-white shadow" />
        </div>
      </div>
    )
  }

  if (themeId === 2 || themeId === 3) {
    return (
      <div className="flex h-full min-h-[140px] overflow-hidden rounded-lg border bg-white">
        <div className="flex w-1/2 items-center justify-center bg-slate-50">
          <div className="h-16 w-12 rounded-md bg-white shadow" />
        </div>
        <div className="relative w-1/2" style={{ background: accent }}>
          {photo ? <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" /> : null}
        </div>
      </div>
    )
  }

  if (themeId === 7) {
    return (
      <div className="flex h-full min-h-[140px] overflow-hidden rounded-lg border bg-slate-50">
        <div className="flex w-1/2 items-center justify-center">
          <div className="h-16 w-12 rounded-md bg-white shadow" />
        </div>
        <div className="relative m-2 w-1/2 overflow-hidden rounded-md bg-emerald-50">
          {photo ? <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" /> : null}
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex h-full min-h-[140px] items-center justify-center overflow-hidden rounded-lg border bg-slate-50">
      {photo ? <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" /> : null}
      <div className="relative h-16 w-14 rounded-md bg-white shadow" />
    </div>
  )
}

export function LoginPageDesignFields({ value, onChange, error }: LoginPageDesignFieldsProps) {
  const [imageError, setImageError] = useState<string | null>(null)
  const [imageBusy, setImageBusy] = useState(false)

  const previewImage = useMemo(() => resolveLoginThemeImage(value), [value])

  async function handleImage(file: File | null) {
    if (!file) return
    if (!file.type.startsWith("image/")) {
      setImageError("Please upload an image file.")
      return
    }
    setImageBusy(true)
    setImageError(null)
    try {
      const result = await fileToCompressedDataUrl(file)
      onChange({
        ...value,
        imageUrl: result.dataUrl,
        accentColor: result.accentColor,
      })
    } catch (err: any) {
      setImageError(err?.message || "Could not process the image.")
    } finally {
      setImageBusy(false)
    }
  }

  return (
    <div className="md:col-span-2 space-y-4 rounded-lg border bg-muted/20 p-4">
      <div className="flex items-start gap-3">
        <LayoutTemplate className="mt-0.5 h-5 w-5 text-muted-foreground" />
        <div>
          <div className="text-sm font-semibold">Login page design</div>
          <p className="text-xs text-muted-foreground mt-1">
            Choose an existing theme and optionally upload a company image. The login page uses that
            theme plus your image to create a unique look for this client.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {LOGIN_PAGE_THEMES.map((theme) => {
          const selected = value.themeId === theme.id
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => onChange({ ...value, themeId: theme.id })}
              className={`text-left rounded-lg border bg-white p-2 transition ${
                selected ? "ring-2 ring-sky-500 border-sky-300" : "hover:border-slate-300"
              }`}
            >
              <div className="h-20 overflow-hidden rounded-md">
                <ThemePreview
                  themeId={theme.id}
                  imageUrl={value.imageUrl || theme.defaultImage}
                  accentColor={value.accentColor || "#0284c7"}
                />
              </div>
              <div className="mt-2 text-xs font-semibold">{theme.name}</div>
              <div className="text-[11px] text-muted-foreground leading-snug">{theme.description}</div>
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),minmax(0,1.1fr)] gap-4">
        <div className="space-y-2">
          <div className="text-sm font-medium">Upload image</div>
          <label className="flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed bg-white px-3 py-3 text-sm text-muted-foreground hover:bg-slate-50">
            <ImagePlus className="h-4 w-4" />
            {imageBusy ? "Processing image..." : "Choose an image"}
            <Input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={imageBusy}
              onChange={(event) => {
                const file = event.target.files?.[0] || null
                void handleImage(file)
                event.target.value = ""
              }}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            JPG, PNG, or WebP. The image is placed into the selected theme.
          </p>
          {value.imageUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...value, imageUrl: null, accentColor: null })}
            >
              Use theme default image
            </Button>
          )}
          {(imageError || error) && <div className="text-xs text-red-600">{imageError || error}</div>}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Preview</div>
          <div className="overflow-hidden rounded-lg border bg-white p-2">
            <ThemePreview
              themeId={value.themeId}
              imageUrl={previewImage}
              accentColor={value.accentColor || "#0284c7"}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
